import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  MapPin, 
  Heart,
  UserCog,
  Building2, 
  AlertTriangle, 
  FileText,
  ClipboardList,
  MessageSquare,
  LogOut,
  Menu,
  X,
  Trash2
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
  currentPage?: string;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  current: boolean;
  comingSoon?: boolean;
}

interface NavigationSection {
  title: string;
  items: NavigationItem[];
}

const Layout: React.FC<LayoutProps> = ({ children, currentPage }) => {
  const { admin, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Auto-detect current page from URL if not provided
  const getCurrentPage = () => {
    if (currentPage) return currentPage;
    
    const path = window.location.pathname;
    if (path === '/') return 'dashboard';
    if (path.startsWith('/strayyos')) return 'strayyos';
    if (path.startsWith('/angels')) return 'angels';
    if (path.startsWith('/users/deletions')) return 'deletions';
    if (path.startsWith('/users')) return 'users';
    if (path.startsWith('/resources')) return 'resources';
    if (path.startsWith('/emergencies')) return 'emergencies';
    if (path.startsWith('/claims')) return 'claims';
    if (path.startsWith('/reports')) return 'reports';
    if (path.startsWith('/adoptions')) return 'adoptions';
    return 'dashboard';
  };

  const detectedCurrentPage = getCurrentPage();

  // Grouped navigation structure
  const navigationSections: NavigationSection[] = [
    {
      title: 'Overview',
      items: [
        { name: 'Dashboard', href: '/', icon: LayoutDashboard, current: detectedCurrentPage === 'dashboard' },
      ]
    },
    {
      title: 'Core Management',
      items: [
        { name: 'Strayyos', href: '/strayyos', icon: MapPin, current: detectedCurrentPage === 'strayyos' },
        { name: 'Angels', href: '/angels', icon: Heart, current: detectedCurrentPage === 'angels' },
        { name: 'Resources', href: '/resources', icon: Building2, current: detectedCurrentPage === 'resources' },
        { name: 'Adoptions', href: '/adoptions', icon: Heart, current: detectedCurrentPage === 'adoptions' },
      ]
    },
    {
      title: 'User Management',
      items: [
        { name: 'Users', href: '/users', icon: UserCog, current: detectedCurrentPage === 'users' },
        { name: 'Account Deletions', href: '/users/deletions', icon: Trash2, current: detectedCurrentPage === 'deletions' },
      ]
    },
    {
      title: 'Operations',
      items: [
        { name: 'Emergencies', href: '/emergencies', icon: AlertTriangle, current: detectedCurrentPage === 'emergencies', comingSoon: true },
        { name: 'Claims', href: '/claims', icon: ClipboardList, current: detectedCurrentPage === 'claims', comingSoon: true },
        { name: 'Reports', href: '/reports', icon: MessageSquare, current: detectedCurrentPage === 'reports' },
      ]
    },
  ];

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Sidebar for mobile */}
      <div className={`lg:hidden ${sidebarOpen ? 'fixed inset-0 z-40' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          <SidebarContent navigationSections={navigationSections} admin={admin} onLogout={handleLogout} />
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-64">
          <SidebarContent navigationSections={navigationSections} admin={admin} onLogout={handleLogout} />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <div className="lg:hidden">
          <div className="flex items-center justify-between bg-white border-b border-gray-200 px-4 py-1.5">
            <button
              className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-orange-500"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Strayyo Admin</h1>
            </div>
            <div className="w-12" />
          </div>
        </div>
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          {children}
        </main>
      </div>
    </div>
  );
};

interface SidebarContentProps {
  navigationSections: NavigationSection[];
  admin: any;
  onLogout: () => void;
}

const SidebarContent: React.FC<SidebarContentProps> = ({ navigationSections, admin, onLogout }) => {
  return (
    <div className="flex flex-col flex-grow pt-5 pb-4 overflow-y-auto bg-white border-r border-gray-200">
      {/* Logo */}
      <div className="flex items-center flex-shrink-0 px-4 mb-8">
        <h1 className="text-2xl font-bold text-orange-600">Strayyo</h1>
        <span className="ml-2 px-2 py-0.5 text-xs font-medium text-orange-700 bg-orange-100 rounded">Admin</span>
      </div>

      {/* Navigation Sections */}
      <div className="flex-grow flex flex-col space-y-6">
        {navigationSections.map((section) => (
          <nav key={section.title} className="px-3">
            <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              {section.title}
            </h3>
            <div className="space-y-1">
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    className={`${
                      item.current
                        ? 'bg-orange-50 text-orange-700 border-l-4 border-orange-500'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-l-4 border-transparent'
                    } group flex items-center px-3 py-2 text-sm font-medium rounded-r-md transition-colors duration-150`}
                  >
                    <Icon
                      className={`${
                        item.current ? 'text-orange-600' : 'text-gray-400 group-hover:text-gray-600'
                      } mr-3 h-5 w-5 flex-shrink-0`}
                    />
                    <span className="flex-1">{item.name}</span>
                    {item.comingSoon && (
                      <span className="ml-2 px-2 py-0.5 text-xs font-medium text-gray-500 bg-gray-100 rounded">
                        Soon
                      </span>
                    )}
                  </a>
                );
              })}
            </div>
          </nav>
        ))}
      </div>

      {/* User Profile & Logout */}
      <div className="flex-shrink-0 border-t border-gray-200 p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-sm">
              <span className="text-white font-semibold text-sm">
                {admin?.name?.charAt(0) || admin?.email?.charAt(0) || 'A'}
              </span>
            </div>
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{admin?.name || 'Admin'}</p>
            <p className="text-xs text-gray-500 truncate">{admin?.email}</p>
          </div>
          <button
            onClick={onLogout}
            className="ml-2 flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors duration-150"
            title="Logout"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Layout;
